import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils/cn';
import { iconSize, rowBase, rowIcon, rowInactive, rowSelected } from './styles';
import type { NavItem } from './types';

/**
 * A first-level row that is a page in its own right (Home, Network) or an
 * external link (Help Center, HRDD). It has no second level, so it carries the
 * selected state itself rather than handing it to a child.
 */
export function ColumnItem({
  item,
  active,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  // The column shows the full name — `shortLabel` exists for the rail, where
  // there is no room for one.
  const label = item.label;
  const inner = (
    <>
      <span className={rowIcon}>
        {/* No accent class here — the row carries the colour and the icon
            inherits it, so the two can never disagree. */}
        <FontAwesomeIcon icon={item.icon} className={iconSize} />
      </span>
      <span className='min-w-0 flex-1 truncate'>{label}</span>
    </>
  );

  // `accent` is documented as icon *and* label, and the rail already renders it
  // that way (it has only an icon). In the column the label was being left at
  // the resting foreground, so the entry read half-accented. Skipped while the
  // row is selected: the selected surface owns the colour then.
  const cls = cn(
    rowBase,
    active ? rowSelected : rowInactive,
    item.accent && !active && 'text-brand-accent hover:text-brand-accent',
    item.className,
  );

  if (item.onClick) {
    return (
      <button
        type='button'
        aria-label={label}
        className={cls}
        onClick={() => {
          item.onClick?.();
          onNavigate?.();
        }}
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      to={item.href as never}
      aria-label={label}
      onClick={onNavigate}
      className={cls}
      aria-current={active ? 'page' : undefined}
    >
      {inner}
    </Link>
  );
}
